import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, DocumentIntegrityModal, ContractTimeline, ProviderContractsTab, ProjectWorkflowStatusBadge } from '../components';
import { ProjectStatusOverview } from '../components/ProjectStatusOverview';
import { projectStatusOverview } from '../utils/projectStatusOverview';
import { ProjectDocumentsTab } from '../components/ProjectDocumentsTab';
import { contractFolderHierarchy } from '../utils/agreementIdentity';
import { ProjectParticipantsTab } from '../components/ProjectParticipantsTab';
import { ProjectTabsBar } from '../components/ProjectTabsBar';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProjectCompanyName } from '../components/ProjectCompanyName';
import { projectApi, contractApi, milestoneApi, documentApi, marketplaceApi, projectStatusApi } from '../services/api';
import { type ProjectStatusInstance } from '../services/projectStatusUtils';
import { useProjectTabNavigation } from '../hooks/useProjectTabNavigation';
import { projectDisplayStatus } from '../utils/milestoneWorkflowStatus';
import type { Contract, Milestone, DocumentRecord } from '../types';
import { timelineContracts, timelineMilestones } from '../utils/timelineContracts';

type TabKey = 'contract' | 'participants' | 'documents' | 'timeline';

const PROJECT_VIEW_TABS: readonly TabKey[] = [
  'contract',
  'participants',
  'documents',
  'timeline'
];

interface ProviderProjectViewProps {
  providerType: 'lawyer' | 'inspector';
}

/** Shared project detail for both read-only provider personas. */
export function ProviderProjectView({ providerType }: ProviderProjectViewProps) {
  const { t } = useTranslation();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const viewTranslationPrefix = `${providerType}ProjectView`;
  const listTranslationPrefix = `${providerType}Projects`;
  const projectsRoute = `/${providerType}/projects`;

  const [project, setProject] = useState<any>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [contractMilestonesMap, setContractMilestonesMap] = useState<Record<string, Milestone[]>>({});
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [folderTree, setFolderTree] = useState<any>(null);
  const [activeTab, selectTab] = useProjectTabNavigation(PROJECT_VIEW_TABS, 'timeline');
  const [isLoading, setIsLoading] = useState(true);
  const [attachmentDetailsTarget, setAttachmentDetailsTarget] = useState<DocumentRecord | null>(null);
  const [participants, setParticipants] = useState<any>(null);
  const [projectStatusInstance, setProjectStatusInstance] = useState<ProjectStatusInstance | null>(null);
  const projectStatusSteps = projectStatusInstance?.steps || [];
  const projectMilestones = timelineMilestones(contracts, Object.values(contractMilestonesMap).flat());
  const displayStatus = project
    ? projectDisplayStatus(project, projectMilestones)
    : null;

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (projectId && activeTab === 'participants') {
      // Same two-source combination as the company view: parties gives the
      // customer + company, the marketplace endpoint gives the providers. Both
      // are already scoped to what this role may see by the API.
      Promise.all([
        projectApi.getProjectParties(projectId).catch(() => null),
        marketplaceApi.getProjectParticipants(projectId).catch(() => null),
      ]).then(([parties, marketplaceParticipants]) => {
        setParticipants({
          customer: parties?.customer ?? null,
          company: parties?.company ?? null,
          providers: marketplaceParticipants?.providers ?? [],
        });
      }).catch(() => {});
    }
  }, [projectId, activeTab]);

  useEffect(() => {
    if (projectId && activeTab === 'documents') {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, activeTab]);

  const loadProject = async () => {
    try {
      const data = await projectApi.getProject(projectId!);
      setProject(data);
      try {
        const statusData = await projectStatusApi.getInstance(projectId!);
        setProjectStatusInstance(statusData);
      } catch {}
      const contractData = await contractApi.getContracts(projectId!);
      setContracts(contractData);
      // Load milestones per contract so the phase timeline (ContractTimeline)
      // can render the same visualisation the company/person see.
      const milestonesMap: Record<string, Milestone[]> = {};
      await Promise.all(
        contractData.map(async (contract) => {
          try {
            milestonesMap[contract.id] = await milestoneApi.getMilestones(contract.id);
          } catch {
            milestonesMap[contract.id] = [];
          }
        }),
      );
      setContractMilestonesMap(milestonesMap);
    } catch (error) {
      console.error('Failed to load project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all documents the provider is authorised to see (backend scopes by
  // role) plus the folder tree. The shared ProjectDocumentsTab filters by the
  // selected folder client-side, mirroring the company view.
  const loadDocuments = async () => {
    if (!projectId) return;
    try {
      const [tree, docs] = await Promise.all([
        documentApi.getFolderTree(projectId),
        documentApi.listDocuments(projectId),
      ]);
      setFolderTree(tree);
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const currentContracts = timelineContracts(contracts);
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);

  // "Projektstatus": the shared overview in its read-only viewer mode — the
  // contracts' state and value, without payments or actions.
  const renderStatusOverview = () => {
    const overview = projectStatusOverview({
      frames: currentContracts,
      contracts,
      milestonesByContract: contractMilestonesMap,
      side: 'viewer',
      projectFunded: false,
      projectClosed: project?.status === 'completed' || displayStatus === 'external_dispute'
    });
    const noop = () => {};
    return (
      <ProjectStatusOverview
        rows={overview.rows}
        totals={overview.totals}
        side="viewer"
        statusBadge={
          <ProjectWorkflowStatusBadge project={project} milestones={projectMilestones} steps={projectStatusSteps} />
        }
        notices={
          <p className="project-status-company">
            <i className="feather-briefcase" aria-hidden="true"></i>
            <ProjectCompanyName companyName={project?.companyName} />
          </p>
        }
        menuItems={[]}
        formatCurrency={formatCurrency}
        onOpenMilestone={noop}
        onSign={noop}
        onPay={noop}
        onShowContract={root => document.querySelector(`[data-contract-group="${root.id}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      />
    );
  };

  // "Kontraktstidslinjer": the shared contract frames, edge to edge with the
  // start page's header — read-only, so no actions, payments or totals.
  const renderContractTimelines = () => currentContracts.length === 0 ? (
    <Card className="mb-4" title={t('projectView.contractTimelines.title', 'Kontraktstidslinjer')} icon="file-text">
      <div className="text-center py-4 text-muted">
        <i className="feather-git-branch fs-1 d-block mb-3" aria-hidden="true"></i>
        <h6 className="fw-bold mb-2">{t('projectView.contractTimelines.emptyTitle', 'Ingen tidslinje ännu')}</h6>
        <p className="mb-0">{t('projectView.contractTimelines.emptyDesc', 'Processvisualiseringen visas när projektet har kontrakt och etappmål.')}</p>
      </div>
    </Card>
  ) : (
    <section id="contract-timelines" className="card project-status mb-4" aria-labelledby="contract-timelines-title">
      <header className="project-status-header">
        <h2 id="contract-timelines-title" className="project-status-title">
          <i className="feather-file-text" aria-hidden="true"></i>
          {t('projectView.contractTimelines.title', 'Kontraktstidslinjer')}
        </h2>
      </header>
      <div className="project-section-body contract-frames">
        {currentContracts.map(contract => (
          <div key={contract.id}>
            <ContractTimeline
              contract={contract}
              relatedContracts={contracts}
              relatedMilestones={Object.values(contractMilestonesMap).flat()}
              milestones={contractMilestonesMap[contract.id] || []}
              parentContract={contract.parentContractId ? contracts.find((x) => x.id === contract.parentContractId) || null : null}
              projectCompleted={project?.status === 'completed'}
              projectExternallyDisputed={project?.status === 'dispute' || displayStatus === 'external_dispute'}
              frameSlots={{ compactHeader: true }}
            />
          </div>
        ))}
      </div>
    </section>
  );

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'timeline', label: t(`${viewTranslationPrefix}.tabs.timeline`, 'Tidslinje'), icon: 'git-branch' },
    { key: 'contract', label: t(`${viewTranslationPrefix}.tabs.contract`, 'Kontrakt'), icon: 'file-text' },
    { key: 'participants', label: t('projectView.tabs.participants', 'Deltagare'), icon: 'users' },
    { key: 'documents', label: t(`${viewTranslationPrefix}.tabs.documents`, 'Dokument'), icon: 'folder' }
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <Card>
          <div className="text-center py-5 text-muted">
            <i className="feather-alert-circle fs-1 d-block mb-3"></i>
            <h5>{t(`${viewTranslationPrefix}.notFound`, 'Projekt hittades inte')}</h5>
            <button className="btn btn-primary mt-3" onClick={() => navigate(projectsRoute)}>
              <i className="feather-arrow-left me-2"></i>
              {t(`${viewTranslationPrefix}.backToList`, 'Tillbaka till projekt')}
            </button>
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {t(`${viewTranslationPrefix}.title`, 'Projekt')} #{project.projectNumber}
            </h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t(`${listTranslationPrefix}.title`, 'Projekt'), path: projectsRoute },
            { label: `#${project.projectNumber}` }
          ]} />
        </div>
        <div className="page-header-right ms-auto">
          <ProjectWorkflowStatusBadge
            project={project}
            milestones={projectMilestones}
            steps={projectStatusSteps}
            className="fs-12"
          />
        </div>
      </div>

      {/* Read-only notice */}
      <div className="alert alert-info py-2 px-3 mb-4 d-flex align-items-center gap-2">
        <i className="feather-eye"></i>
        <small>{t(`${viewTranslationPrefix}.readOnlyNotice`, 'Du har läsbehörighet till detta projekt. Redigering är inte tillgänglig.')}</small>
      </div>

      {/* Shared tab bar — same visual language and mobile behaviour as the company view. */}
      <ProjectTabsBar tabs={tabs} activeKey={activeTab} onSelect={(k) => selectTab(k as TabKey)} />

      {/* Contract Tab — shared read-only contract list + details (jurist) */}
      {activeTab === 'contract' && (
        <ProviderContractsTab
          projectId={projectId!}
          contracts={contracts}
          contractMilestonesMap={contractMilestonesMap}
        />
      )}

      {/* Participants Tab */}
      {activeTab === 'participants' && (
        <ProjectParticipantsTab participants={participants} />
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <ProjectDocumentsTab
          projectId={projectId!}
          documents={documents}
          folderTree={folderTree}
          canUpload={false}
          canRename={false}
          canDelete={false}
          isAdmin={false}
          onReload={loadDocuments}
          contractFolders={contractFolderHierarchy(contracts)}
          onShowFileInfo={setAttachmentDetailsTarget}
        />
      )}
      {/* Timeline Tab */}
      {activeTab === 'timeline' && project && (
        <div className="tab-pane fade show active">
          {renderStatusOverview()}
          {renderContractTimelines()}
        </div>
      )}

      {attachmentDetailsTarget && (
        <DocumentIntegrityModal
          document={attachmentDetailsTarget}
          onClose={() => setAttachmentDetailsTarget(null)}
        />
      )}
    </DashboardLayout>
  );
}
